import * as React from 'react'
import {
	readDismissedFromLocalStorage,
	shouldShowLowConfidenceNudge,
	writeDismissedToLocalStorage,
} from '@/components/project/artifact-viewer/transcript/utils/audioQualityNudge'
import { AUDIO_QUALITY_CONFIDENCE_THRESHOLD } from '@/constants/transcription'

export interface UseAudioQualityNudgeParams {
	artifactId: string | null | undefined
	/** Overall transcript confidence from API metadata (0–1), or null/undefined if unknown */
	confidence: number | null | undefined
	/** Transcript fetch status from API */
	transcriptStatus: 'processing' | 'ready' | 'error' | undefined
	loading: boolean
	error: string | null
}

export interface UseAudioQualityNudgeResult {
	/** True when the yellow nudge should be visible */
	showNudge: boolean
	dismiss: () => void
	/** True when we reserve top space (ready transcript, no error, eligible confidence) */
	reserveNudgeSlot: boolean
}

/**
 * Per-artifact dismissal: localStorage when available, else in-memory for the session.
 */
export function useAudioQualityNudge({
	artifactId,
	confidence,
	transcriptStatus,
	loading,
	error,
}: UseAudioQualityNudgeParams): UseAudioQualityNudgeResult {
	const [sessionDismissed, setSessionDismissed] = React.useState(false)

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset dismissal state when viewing a different artifact
	React.useEffect(() => {
		setSessionDismissed(false)
	}, [artifactId])

	const storageDismissed = React.useMemo(() => {
		if (!artifactId) return false
		return readDismissedFromLocalStorage(artifactId)
	}, [artifactId])

	const eligible =
		Boolean(artifactId) &&
		!loading &&
		!error &&
		transcriptStatus === 'ready' &&
		shouldShowLowConfidenceNudge(confidence, AUDIO_QUALITY_CONFIDENCE_THRESHOLD)

	const dismissed = storageDismissed || sessionDismissed

	const showNudge = Boolean(eligible && !dismissed)

	const reserveNudgeSlot = Boolean(eligible)

	const dismiss = React.useCallback(() => {
		if (!artifactId) return
		writeDismissedToLocalStorage(artifactId)
		setSessionDismissed(true)
	}, [artifactId])

	return { showNudge, dismiss, reserveNudgeSlot }
}
